import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Chip,
  Grid,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  IconButton
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { format } from 'date-fns'
import { pl } from 'date-fns/locale'
import { useTicket } from '@services/helpdesk/helpdesk.hooks'
import HelpdeskRepairCosts from './HelpdeskRepairCosts'
import HelpdeskInternalComments from './HelpdeskInternalComments'
import HelpdeskMessages from './HelpdeskMessages'

// Mapping of statuses to Polish names and colors
const statusMap: Record<
  string,
  {
    label: string
    color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
  }
> = {
  NEW: { label: 'Nowy', color: 'primary' },
  IN_PROGRESS: { label: 'W trakcie', color: 'info' },
  WAITING_FOR_PARTS: { label: 'Oczekiwanie na części', color: 'warning' },
  DONE: { label: 'Zakończony', color: 'success' },
  CANCELLED: { label: 'Anulowany', color: 'error' }
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`ticket-tabpanel-${index}`}
      aria-labelledby={`ticket-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  )
}

function a11yProps(index: number) {
  return {
    id: `ticket-tab-${index}`,
    'aria-controls': `ticket-tabpanel-${index}`
  }
}

const HelpdeskTicketDetails = () => {
  const { id } = useParams<{ id: string }>()
  const ticketId = id ? parseInt(id, 10) : 0
  const navigate = useNavigate()
  const { data: ticket, isLoading, error } = useTicket(ticketId)
  const [tabValue, setTabValue] = useState(0)

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  const handleBack = () => {
    navigate('/helpdesk/tickets')
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity='error' sx={{ mt: 2 }}>
        Wystąpił błąd podczas ładowania zgłoszenia: {(error as Error).message}
      </Alert>
    )
  }

  if (!ticket) {
    return (
      <Alert severity='warning' sx={{ mt: 2 }}>
        Nie znaleziono zgłoszenia o ID {ticketId}
      </Alert>
    )
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBack} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant='h4' component='h1'>
          Szczegóły zgłoszenia
        </Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
            >
              <Typography variant='h5' component='h2'>
                {ticket.topic}
              </Typography>
              <Chip
                label={statusMap[ticket.status]?.label || ticket.status}
                color={statusMap[ticket.status]?.color || 'default'}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant='subtitle2' color='text.secondary'>
              Inwestycja
            </Typography>
            <Typography variant='body1' gutterBottom>
              {ticket.investmentName}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant='subtitle2' color='text.secondary'>
              Lokal
            </Typography>
            <Typography variant='body1' gutterBottom>
              {ticket.subjectName}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant='subtitle2' color='text.secondary'>
              Kategoria usterki
            </Typography>
            <Typography variant='body1' gutterBottom>
              {ticket.faultCategoryName}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant='subtitle2' color='text.secondary'>
              Data naprawy
            </Typography>
            <Typography variant='body1' gutterBottom>
              {ticket.dateRepair
                ? format(new Date(ticket.dateRepair), 'dd.MM.yyyy', { locale: pl })
                : 'Nie ustalono'}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          <Grid item xs={12}>
            <Typography variant='subtitle2' color='text.secondary'>
              Opis
            </Typography>
            <Typography variant='body1' sx={{ whiteSpace: 'pre-wrap' }}>
              {ticket.description}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant='caption' color='text.secondary'>
                Utworzono: {format(new Date(ticket.createdAt), 'dd.MM.yyyy HH:mm', { locale: pl })}
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                Ostatnia aktualizacja:{' '}
                {format(new Date(ticket.updatedAt), 'dd.MM.yyyy HH:mm', { locale: pl })}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label='ticket tabs'
            variant='fullWidth'
            sx={{
              '& .MuiTab-root': {
                minWidth: '180px',
                fontWeight: 500,
                fontSize: '1rem',
                py: 1.5
              }
            }}
          >
            <Tab label='Zadania do wykonania' {...a11yProps(0)} />
            <Tab label='Komentarze wewnętrzne' {...a11yProps(1)} />
            <Tab label='Wiadomości' {...a11yProps(2)} />
          </Tabs>
        </Box>
        <TabPanel value={tabValue} index={0}>
          <HelpdeskRepairCosts ticketId={ticketId} />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <HelpdeskInternalComments ticketId={ticketId} />
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          <HelpdeskMessages ticketId={ticketId} />
        </TabPanel>
      </Box>
    </Box>
  )
}

export default HelpdeskTicketDetails
